import {
  Enum_Commande_Status,
  GetClasseWithNoteAndStudentsOrdersQuery,
  GetShopEleveQuery,
  GetUserClasseQuery,
  GetUserOnHomeQuery,
  ProfDashboardUser,
  QuerySoftSkillsQuery,
  QueryUserAccountQuery,
  UserNote
} from '../../../graphql/generated/graphql';
import { ToRequiredType } from '../../../types/typeDef';
import { ROLE, USER_ROLE } from '../../user/authorization';
import {
  ProfileInfo,
  ProfileInfoExtended,
  UserPerfs,
  UserState
} from '../../../types/user/user.def';
import { AvatarMedia } from '../../../types/avatar/avatarList.def';
import { ElevesCommandesStatus } from '../../../domain/commission/commission';
import { ClasseDef } from '../../../types/classe/classe.def';

export const getUserStateFromQuery: (
  user: GetShopEleveQuery['eleve']
) => UserState = user => ({
  ...(user as ToRequiredType<typeof user>),
  note: { updatedAt: user.note?.updatedAt },
  classe: {
    ...(user.classe as ToRequiredType<typeof user.classe>),
    lycee: { name: user.classe?.lycee?.name, ville: user.classe?.lycee?.ville }
  },
  role: USER_ROLE as ROLE
});

export const getUserStateFromQueryAccount: (
  user: QueryUserAccountQuery['getUserAccount']
) => ProfileInfo = user => ({
  id: user.id,
  avatar: user.avatar,
  email: user.email,
  firstname: user.firstname,
  lastname: user.lastname,
  username: user.username,
  ville: user.ville,
  tel: user.tel,
  shop: user.shop,
  classe: {
    ...(user.classe as ToRequiredType<ClasseDef>),
    filliere: { name: user.classe.filiere.name }
  },
  note: user.note
});

export const getAvatarStateFromQuery: (
  user: GetUserOnHomeQuery['getUserOnHome']
) => AvatarMedia = user => ({
  media: {
    url: user?.avatar?.media?.url
  }
});

export const getProfileInfoFromQueryDashboardProf: (
  user: ProfDashboardUser
) => ProfileInfo = user => ({
  id: user?.id,
  ...user,
  note: user.note,
  avatar: user.avatar
});

export const getProfileInfoExtendedFromQuery: (
  user: ProfDashboardUser
) => ProfileInfoExtended = user => ({
  ...getProfileInfoFromQueryDashboardProf(user),
  role: user.role.name as ROLE,
  mdp: user?.mdp
});

export const getElevesCommandesStatusFromQuery: (
  users: QuerySoftSkillsQuery['getUserSoftSkills']['classe']['users']
) => ElevesCommandesStatus[] = users =>
  users.map(u => ({
    role: u.role,
    commandes: u.commandes.map(c => ({
      status: c.status as Enum_Commande_Status
    }))
  }));

export const getElevesCommandesStatusFromQueryOng: (
  users: GetClasseWithNoteAndStudentsOrdersQuery['getClasseWithNoteAndEleves']['users']
) => ElevesCommandesStatus[] = users =>
  users.map(u => ({
    commandes: u.commandes.map(c => ({
      status: c.status as Enum_Commande_Status,
      customerContributedToTheOng: c.customerContributedToTheOng
    })),
    qteca: u.note.qteca
  }));

export const getUserPerfsFromQuery: (
  user: QuerySoftSkillsQuery['getUserSoftSkills']
) => UserPerfs = user => {
  const users = user.classe.users;
  return {
    note: user.note as ToRequiredType<UserNote>,
    commandes: user.commandes,
    unpayedOrders: user.unpayedOrders,
    classe: {
      users: getElevesCommandesStatusFromQuery(users),
      note: user.classe.noteclasse,
      commandes: user.classe.commandes,
      unpayedOrders: user.classe.unpayedOrders
    }
  };
};

export const getUserClassesFromQuery: (
  classes: GetUserClasseQuery['usersPermissionsUser']['data']['attributes']['classes']['data']
) => any = classes => {
  return classes.map(classe => ({
    id: classe.id,
    name: classe.attributes.name,
    lycee: classe.attributes.lycee.data.attributes.name,
    operationid: classe.attributes.operationid,
    filiere: classe.attributes.filliere.data.attributes.name
  }));
};
